import React from 'react';
import { Box, Divider, FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { InlineMessage } from '../ui';
import type { LightspeedEcomCluster } from '../../services/integrationApi';
import { maskedSecret, savedSecret } from '../../utils/integrationCredentials';

const T = 'integrations.lightspeedEcom.connect';

export const LIGHTSPEED_ECOM_CLUSTERS: Array<{ value: LightspeedEcomCluster; host: string }> = [
  { value: 'us1', host: 'api.shoplightspeed.com' },
  { value: 'eu1', host: 'api.webshopapp.com' },
];

export const LIGHTSPEED_ECOM_DEFAULT_LANGUAGE = 'us';

interface FormProps {
  credentials: Record<string, any>;
  onChange: (credentials: Record<string, any>) => void;
}

/**
 * Connect form for a Lightspeed eCom (C-Series) shop: the per-shop API key and secret the store
 * creates under Settings > Store Settings > Developers, the cluster, and the shop-language URL
 * segment. Modelled on the BigCommerce key form; there is no OAuth for C-Series.
 */
export const LightspeedEcomCredentialsForm: React.FC<FormProps> = ({ credentials, onChange }) => {
  const { t } = useAppTranslation();
  const set = (patch: Record<string, unknown>) => onChange({ ...credentials, ...patch });
  const cluster = (credentials.cluster as LightspeedEcomCluster) || 'us1';
  const language = typeof credentials.language === 'string' ? credentials.language : LIGHTSPEED_ECOM_DEFAULT_LANGUAGE;
  const languageValid = /^[a-z]{2}$/.test(language);
  // Editing a connected integration: the API never returns the stored key or secret, only the shared
  // `<field>Set` / `<field>Last4` markers, and a blank field keeps the stored value.
  const savedKey = savedSecret(credentials, 'apiKey');
  const secretSaved = savedSecret(credentials, 'apiSecret').saved;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <InlineMessage severity="info">{t(`${T}.where`)}</InlineMessage>
      <TextField
        label={t(`${T}.apiKey`)}
        value={credentials.apiKey || ''}
        onChange={(e) => set({ apiKey: e.target.value.trim() })}
        fullWidth
        required={!savedKey.saved}
        helperText={
          savedKey.saved
            ? savedKey.last4
              ? t(`${T}.keepSavedKey`, { last4: savedKey.last4 })
              : t(`${T}.keepSavedSecret`)
            : undefined
        }
        autoComplete="off"
      />
      <TextField
        label={t(`${T}.apiSecret`)}
        type="password"
        value={credentials.apiSecret || ''}
        onChange={(e) => set({ apiSecret: e.target.value.trim() })}
        fullWidth
        required={!secretSaved}
        helperText={secretSaved ? t(`${T}.keepSavedSecret`) : undefined}
        autoComplete="new-password"
      />
      <FormControl fullWidth>
        <InputLabel id="lightspeed-ecom-cluster-label">{t(`${T}.cluster`)}</InputLabel>
        <Select
          labelId="lightspeed-ecom-cluster-label"
          label={t(`${T}.cluster`)}
          value={cluster}
          onChange={(e) => set({ cluster: e.target.value })}
        >
          {LIGHTSPEED_ECOM_CLUSTERS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {t(`${T}.clusters.${option.value}`)} · {option.host}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>{t(`${T}.clusterHelp`)}</FormHelperText>
      </FormControl>
      <TextField
        label={t(`${T}.language`)}
        value={language}
        onChange={(e) => set({ language: e.target.value.trim().toLowerCase() })}
        error={!languageValid}
        helperText={languageValid ? t(`${T}.languageHelp`) : t(`${T}.languageInvalid`)}
        inputProps={{ maxLength: 2, 'aria-label': t(`${T}.language`) }}
        sx={{ maxWidth: 220 }}
      />
      <Typography variant="caption" color="text.secondary">
        {t(`${T}.permissions`)}
      </Typography>
    </Box>
  );
};

interface ReadViewProps {
  credentials: Record<string, any>;
  renderField: (label: string, value: string | undefined) => React.ReactNode;
}

/** The stored connection for the dialog's read view: the key and secret arrive only as the shared `Set` / `Last4` markers. */
export const LightspeedEcomCredentialsReadView: React.FC<ReadViewProps> = ({ credentials, renderField }) => {
  const { t } = useAppTranslation();
  const cluster = LIGHTSPEED_ECOM_CLUSTERS.find((c) => c.value === credentials.cluster);
  return (
    <Box>
      {renderField(t(`${T}.apiKey`), maskedSecret(credentials, 'apiKey', t(`${T}.secretSaved`)))}
      <Divider />
      {renderField(t(`${T}.apiSecret`), maskedSecret(credentials, 'apiSecret', t(`${T}.secretSaved`)))}
      <Divider />
      {renderField(t(`${T}.cluster`), cluster ? `${t(`${T}.clusters.${cluster.value}`)} · ${cluster.host}` : credentials.cluster)}
      <Divider />
      {renderField(t(`${T}.language`), credentials.language)}
    </Box>
  );
};

export default LightspeedEcomCredentialsForm;
